/** Plain, framework-agnostic core of `useDebouncedSave` (spec §15 —
 * "avoid saving on every pointer movement event"). Coalesces bursty
 * node-drag/edge/viewport changes into one PUT roughly `delayMs` after
 * the last change, exposes a `flushNow` escape hatch for "save
 * immediately" moments (e.g. right before navigating away), and
 * `scheduleAndWaitForSettled` for discrete, user-initiated actions that
 * must know the TRUE backend persistence outcome before declaring
 * success (VistoeraTASK-00300 — Canvas "另存编辑图片" must never show
 * success/close the editor before the new Canvas node is genuinely
 * confirmed-persisted, not merely added to local React state).
 *
 * Extracted out of the React hook (`useDebouncedSave.ts`, now a thin
 * wrapper around this class) specifically so this state machine — the
 * exact mechanism VistoeraTASK-00300's fix depends on — can be unit-
 * tested headlessly in plain Node (this repo's `*.node-test.ts`
 * convention explicitly avoids a jsdom/React-render harness), rather
 * than only ever being exercised indirectly through a full CanvasBoard
 * render.
 *
 * Critical: if a newer payload is scheduled while a PUT is in flight,
 * it must not be dropped. A prior implementation returned early from
 * `flushNow` when a save was already in flight and never re-flushed, so
 * Shot→Generate Video creates (and other rapid commits) could look
 * successful in the UI but vanish on the next GET/refresh.
 */

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error'

export class DebouncedSaveController<T> {
  private save: (payload: T) => Promise<void>
  private delayMs: number
  private onStatusChange: (status: SaveStatus) => void
  private timer: ReturnType<typeof setTimeout> | null = null
  private latestPayload: T | null = null
  private saving = false

  constructor(save: (payload: T) => Promise<void>, delayMs: number, onStatusChange: (status: SaveStatus) => void) {
    this.save = save
    this.delayMs = delayMs
    this.onStatusChange = onStatusChange
  }

  /** Swap in a fresh `save` implementation without resetting any other
   * in-progress state — mirrors the hook's `saveRef.current = save` on
   * every render (so a stale closure over `onSave`/`projectId` etc. is
   * never used for a save that fires after a re-render). */
  setSave(save: (payload: T) => Promise<void>) {
    this.save = save
  }

  cancelPending() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.latestPayload = null
    this.onStatusChange('idle')
  }

  async flushNow(): Promise<boolean> {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    if (this.latestPayload === null) return true
    // In-flight PUT: keep the latest payload; the active flush's own
    // `finally` below will chain another flush when it ends. `false`
    // here means "this call didn't perform a save", not "it failed" —
    // callers that need the TRUE end-to-end outcome must use
    // `scheduleAndWaitForSettled` instead, never treat this as terminal.
    if (this.saving) return false

    const payload = this.latestPayload
    this.latestPayload = null
    this.saving = true
    this.onStatusChange('saving')
    let ok = true
    try {
      await this.save(payload)
      this.onStatusChange('saved')
    } catch {
      ok = false
      this.onStatusChange('error')
    } finally {
      this.saving = false
      if (this.latestPayload !== null) {
        this.onStatusChange('pending')
        this.timer = setTimeout(() => {
          void this.flushNow()
        }, 0)
      }
    }
    return ok
  }

  schedule(payload: T) {
    this.latestPayload = payload
    this.onStatusChange('pending')
    if (this.saving) {
      // Do not start a parallel timer that would no-op and strand the
      // payload — the active flush's `finally` will re-enter flushNow.
      return
    }
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      void this.flushNow()
    }, this.delayMs)
  }

  /** For discrete, user-initiated actions that must know the TRUE
   * backend persistence outcome before declaring success. Schedules
   * `payload` exactly like `schedule`, then waits out any save already
   * in flight (polling, since this controller has no other synchronous
   * "is anything happening" signal) before flushing, and loops if a
   * newer payload slipped in meanwhile (e.g. a concurrent unrelated
   * autosave) so the payload we were asked to wait for is genuinely the
   * one that got persisted (or the one whose failure we report) — never
   * a stale "someone else is saving, so I'll just say true" shortcut.
   * Reuses this SAME state machine end-to-end — no second parallel
   * Canvas-save system. Never used by the ordinary drag/pan/viewport
   * autosave path, which stays fire-and-forget for performance. */
  async scheduleAndWaitForSettled(payload: T, pollMs = 50): Promise<boolean> {
    this.latestPayload = payload
    this.onStatusChange('pending')
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (!this.saving) {
        const ok = await this.flushNow()
        if (this.latestPayload === null) return ok
        // A newer payload replaced ours mid-flush — keep waiting for IT
        // to settle too, since it's a superset of our state.
        continue
      }
      await new Promise((resolve) => setTimeout(resolve, pollMs))
    }
  }

  dispose() {
    // VistoeraTASK-00585 — unmount must not silently drop a pending payload
    // (e.g. paste → navigate to Text Assets within the debounce window).
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    if (this.latestPayload !== null && !this.saving) {
      void this.flushNow()
    }
  }
}
